import { Section, CodeBlock, InfoBox } from "@/components"

export function NextTailwindComponentsPage() {
  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">Components</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          Explore custom UI components built with Tailwind CSS for Next.js. Learn how to create 
          reusable components that work with both Server and Client Components.
        </p>
      </div>

      <Section title="Component Patterns" description="Building with Tailwind">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            Components are styled with Tailwind utility classes. Server Components by default, 
            add <code className="bg-muted px-2 py-1 rounded">"use client"</code> only when needed.
          </p>
        </div>
      </Section>

      <Section title="Common Components" description="Pre-built examples">
        <div className="space-y-8">
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Button Component</h3>
            <p className="text-muted-foreground">
              Flexible button with variants and sizes using Tailwind utilities.
            </p>
            <CodeBlock
              code={`// components/Button.tsx
"use client" // Needed for onClick

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "danger"
  size?: "sm" | "md" | "lg"
  children: React.ReactNode
}

export function Button({ 
  variant = "primary", 
  size = "md", 
  children, 
  className = "",
  ...props 
}: ButtonProps) {
  const baseClasses = "inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
  
  const variantClasses = {
    primary: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600",
    secondary: "bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600",
    danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500"
  }
  
  const sizeClasses = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-base",
    lg: "px-6 py-3 text-lg"
  }

  return (
    <button
      className={\`\${baseClasses} \${variantClasses[variant]} \${sizeClasses[size]} \${className}\`}
      {...props}
    >
      {children}
    </button>
  )
}`}
              language="typescript"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Card Component (Server Component)</h3>
            <p className="text-muted-foreground">
              Static card component that doesn't need interactivity.
            </p>
            <CodeBlock
              code={`// components/Card.tsx
// No "use client" - Server Component by default

interface CardProps {
  title: string
  description: string
  children?: React.ReactNode
  className?: string
}

export function Card({ title, description, children, className = "" }: CardProps) {
  return (
    <div className={\`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 \${className}\`}>
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
        {title}
      </h3>
      <p className="text-gray-600 dark:text-gray-400 mb-4">
        {description}
      </p>
      {children}
    </div>
  )
}

// Usage in a Server Component:
export default async function DashboardPage() {
  const data = await getData()
  
  return (
    <div className="grid gap-6">
      <Card title="Total Users" description={\`\${data.users} active users\`} />
      <Card title="Revenue" description={\`$\${data.revenue}\`} />
    </div>
  )
}`}
              language="typescript"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Interactive Form Component</h3>
            <p className="text-muted-foreground">
              Client component for forms with real-time validation.
            </p>
            <CodeBlock
              code={`// components/ContactForm.tsx
"use client"

import { useState } from "react"

export function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    try {
      await fetch("/api/contact", {
        method: "POST",
        body: JSON.stringify(formData)
      })
      // Handle success
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Name
        </label>
        <input
          id="name"
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
          required
        />
      </div>
      
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Email
        </label>
        <input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
          required
        />
      </div>
      
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? "Sending..." : "Send Message"}
      </button>
    </form>
  )
}`}
              language="typescript"
            />
          </div>
        </div>
      </Section>

      <Section title="Server Actions with Forms" description="Next.js 14 pattern">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            Use Server Actions for form mutations without needing API routes or Client Components.
          </p>
          
          <CodeBlock
            code={`// app/contact/actions.ts
"use server"

import { revalidatePath } from "next/cache"

export async function sendContactMessage(formData: FormData) {
  const name = formData.get("name") as string
  const email = formData.get("email") as string
  const message = formData.get("message") as string

  // Send email or save to database
  await sendEmail({ name, email, message })

  revalidatePath("/contact")
  return { success: true }
}

// app/contact/page.tsx (Server Component)
import { sendContactMessage } from "./actions"

export default function ContactPage() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <h1 className="text-4xl font-bold mb-8">Contact Us</h1>
      
      <form action={sendContactMessage} className="space-y-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium mb-2">
            Name
          </label>
          <input
            id="name"
            name="name"
            type="text"
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-800"
            required
          />
        </div>
        
        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-2">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-800"
            required
          />
        </div>
        
        <div>
          <label htmlFor="message" className="block text-sm font-medium mb-2">
            Message
          </label>
          <textarea
            id="message"
            name="message"
            rows={5}
            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-800"
            required
          />
        </div>
        
        <button
          type="submit"
          className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Send Message
        </button>
      </form>
    </div>
  )
}`}
            language="typescript"
          />
        </div>
      </Section>

      <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 space-y-4">
        <h2 className="text-2xl font-bold">Component Best Practices</h2>
        <ul className="space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Start with Server Components, only add "use client" when needed</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use Tailwind utility classes for consistent styling</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Support dark mode with dark: prefixes</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use Server Actions for forms when possible</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Keep components focused and composable</span>
          </li>
        </ul>
      </div>
    </div>
  )
}
